/**
 * useStagedFile — a chosen file, counted as an unsaved change.
 *
 * THE PROBLEM IT SOLVES IS THAT A `File` IS NOT A STRING. The group's draft is
 * `Record<string, string>` — deliberately, because the reducer's four rules are about text an
 * admin typed and are provable over it — so a staged file held in its own `useState` beside the
 * group would be invisible to `isDirty`: the commit bar would not rise, the eyebrow would not say
 * `· Unsaved`, and the file would sit there until the page was closed on it.
 *
 * SO THE FILE'S IDENTITY GOES INTO THE DRAFT AND THE FILE ITSELF STAYS OUT. `name:size` is enough
 * to make the group dirty, enough to make choosing a different file dirty again, and it is what
 * the commit bar counts as one of its fields.
 *
 * IT CLEARS ITSELF ON BOTH ENDINGS. `Discard` puts the marker back to the server's empty value and
 * the file goes with it; a successful commit stamps `savedAt` and the file goes with that. Either
 * way the row falls back to showing what is actually on file, which after the refetch is the
 * document that was just uploaded.
 */

import { useCallback, useEffect, useState } from 'react';

import type { InlineGroup } from './useInlineGroup';

export interface StagedFile {
    file: File | null;
    stage: (file: File | null) => void;
}

export function useStagedFile(group: InlineGroup, field: string): StagedFile {
    const [file, setFile] = useState<File | null>(null);
    const marker = group.draft[field] ?? '';
    const savedAt = group.savedAt;
    // `set` is the hook's stable dispatcher; taking it off `group` keeps `stage` stable too.
    const set = group.set;

    const stage = useCallback(
        (next: File | null) => {
            setFile(next);
            set(field, next ? `${next.name}:${next.size}` : '');
        },
        [set, field],
    );

    // Discarded, or synced back from a server that has no marker: the staged file is gone too.
    useEffect(() => {
        if (marker === '') setFile(null);
    }, [marker]);

    // Committed: the upload has happened, and the row's job is now the stored document.
    useEffect(() => {
        if (savedAt) setFile(null);
    }, [savedAt]);

    return { file, stage };
}
